import styled from '@emotion/styled';
import React from 'react';
import { MarketingAppButtons } from 'components/MarketingPagesShared/MarketingAppButtons';
import { MarketingImage } from 'components/MarketingPagesShared/MarketingImage';
import {
  BREAKPOINT_SMALL,
  MarketingHeading1,
} from 'components/MarketingPagesShared/styles';
import { useT } from 'client/i18n/hooks/useT';
import serverRenderingUtils from 'helpers/serverRenderingUtils';
import {
  HomeSection,
  HomeColumns,
  HomeTextColumn,
  HomeImageColumn,
} from '../../../styles';
import {
  HomeStackedImages,
  CaptionPosition,
  SecondaryImagePosition,
  HomeStackedImagesPrimary,
  HomeStackedImagesSecondary,
  HomeStackedImagesCaption,
} from '../../HomeStackedImages';

export function HomeHero() {
  const t = useT();
  return (
    <HomeHeroSection data-value={serverRenderingUtils.getBaseUrl()}>
      <HomeColumns>
        <HomeTextColumn>
          <HomeHeroTitle>
            {t(
              'HomePage.HomeHero.Title',
              'Say it all with Anchor',
              'Anchor home page title'
            )}
          </HomeHeroTitle>
          <HomeHeroSubtitle>
            {t(
              'HomePage.HomeHero.Message',
              'Create, distribute, and monetize your podcast—all for free.',
              'Anchor home page message'
            )}
          </HomeHeroSubtitle>

          <HomeHeroAppButtons>
            <MarketingAppButtons
              clickEventLocation="Hero"
              isRenderForMobileOnly={true}
            />
          </HomeHeroAppButtons>

          <HomeHeroRightAligned>
            <HomeHeroDesktopImage margin="-100px -100px 0">
              <MarketingImage
                width={100}
                height={100}
                imagePath="home/hero/sibling-rivalry"
                alt={t(
                  'HomePage.HomeHero.PodcastCoverImgAlt',
                  '{podcastName} podcast cover image',
                  'Home page podcast cover image screenreader information',
                  { podcastName: 'The Sibling Rivalry' }
                )}
              />
            </HomeHeroDesktopImage>
          </HomeHeroRightAligned>

          <HomeStackedImages>
            <HomeStackedImagesCaption position={CaptionPosition.BOTTOM}>
              <span
                dangerouslySetInnerHTML={{
                  __html: t(
                    'HomePage.HomeHero.CoHostCaption',
                    '{hostName}, co-host of <strong>“{podcastName}”</strong>',
                    'Home page podcast co-host image caption',
                    {
                      hostName: 'Samir Chaudry',
                      podcastName: 'The Colin and Samir Podcast',
                    }
                  ),
                }}
              />
            </HomeStackedImagesCaption>
            <HomeStackedImagesPrimary>
              <MarketingImage
                width={550}
                height={367}
                imagePath="home/hero/the-colin-and-samir-podcast"
                alt="Photo of Samir recording The Colin and Samir podcast"
              />
            </HomeStackedImagesPrimary>
            <HomeStackedImagesSecondary
              position={SecondaryImagePosition.TOP_LEFT}
            >
              <MarketingImage
                width={100}
                height={100}
                imagePath="home/hero/the-colin-and-samir-podcast-cover"
                alt={t(
                  'HomePage.HomeHero.PodcastCoverImgAlt',
                  '{podcastName} podcast cover image',
                  'Home page podcast cover image screenreader information',
                  { podcastName: 'The Colin and Samir' }
                )}
              />
            </HomeStackedImagesSecondary>
          </HomeStackedImages>
          <HomeHeroDesktopImage size={150} margin="0 0 0 -150px">
            <MarketingImage
              width={150}
              height={150}
              imagePath="home/hero/the-tripping-podcast-cover"
              alt={t(
                'HomePage.HomeHero.PodcastCoverImgAlt',
                '{podcastName} podcast cover image',
                'Home page podcast cover image screenreader information',
                { podcastName: 'The Trippin' }
              )}
            />
          </HomeHeroDesktopImage>
        </HomeTextColumn>
        <HomeHeroImageColumn>
          <HomeSideImageTop>
            <HomeStackedImages>
              <HomeStackedImagesCaption position={CaptionPosition.BOTTOM}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'HomePage.HomeHero.CoHostCaption',
                      '{hostName}, co-host of <strong>“{podcastName}”</strong>',
                      'Home page podcast co-host image caption',
                      {
                        hostName: 'Chris Black',
                        podcastName: 'How Long Gone',
                      }
                    ),
                  }}
                />
              </HomeStackedImagesCaption>
              <HomeStackedImagesPrimary>
                <MarketingImage
                  width={450}
                  height={300}
                  imagePath="home/hero/how-long-gone"
                  alt="Photo of Chris Black recording the How Long Gone podcast"
                />
              </HomeStackedImagesPrimary>
              <HomeStackedImagesSecondary
                position={SecondaryImagePosition.TOP_LEFT}
              >
                <MarketingImage
                  width={100}
                  height={100}
                  imagePath="home/hero/how-long-gone-cover"
                  alt={t(
                    'HomePage.HomeHero.PodcastCoverImgAlt',
                    '{podcastName} podcast cover image',
                    'Home page podcast cover image screenreader information',
                    { podcastName: 'The How Long Gone' }
                  )}
                />
              </HomeStackedImagesSecondary>
            </HomeStackedImages>
          </HomeSideImageTop>
          <HomeStackedImages>
            <HomeStackedImagesCaption position={CaptionPosition.BOTTOM}>
              <span
                dangerouslySetInnerHTML={{
                  __html: t(
                    'HomePage.HomeHero.CoHostsCaption',
                    '{hostNames}, co-hosts of <strong>“{podcastName}”</strong>',
                    'Home page podcast co-hosts image caption',
                    {
                      hostNames: 'Kia Marie & Vic Styles',
                      podcastName: 'Kontent Queens',
                    }
                  ),
                }}
              />
            </HomeStackedImagesCaption>
            <HomeStackedImagesPrimary>
              <MarketingImage
                width={500}
                height={334}
                imagePath="home/hero/kontent-queens"
                alt="Photo of Kia Marie and Vic Styles"
              />
            </HomeStackedImagesPrimary>
            <HomeStackedImagesSecondary
              position={SecondaryImagePosition.TOP_RIGHT}
            >
              <MarketingImage
                width={100}
                height={100}
                imagePath="home/hero/kontent-queens-cover"
                alt={t(
                  'HomePage.HomeHero.PodcastCoverImgAlt',
                  '{podcastName} podcast cover image',
                  'Home page podcast cover image screenreader information',
                  { podcastName: 'The Kontent Queens' }
                )}
              />
            </HomeStackedImagesSecondary>
          </HomeStackedImages>
          <HomeHeroDesktopImage margin="30px 0">
            <MarketingImage
              width={100}
              height={100}
              imagePath="home/hero/couples-therapy-cover"
              alt={t(
                'HomePage.HomeHero.PodcastCoverImgAlt',
                '{podcastName} podcast cover image',
                'Home page podcast cover image screenreader information',
                { podcastName: 'Couples Therapy' }
              )}
            />
          </HomeHeroDesktopImage>
        </HomeHeroImageColumn>
      </HomeColumns>
    </HomeHeroSection>
  );
}

const HomeHeroRightAligned = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const HomeHeroDesktopImage = styled.div<{
  size?: number;
  margin?: string;
}>`
  width: ${({ size = 100 }) => size}px;
  height: ${({ size = 100 }) => size}px;
  margin: ${({ margin = 0 }) => margin};

  @media (max-width: ${BREAKPOINT_SMALL}px) {
    display: none;
  }
`;

const HomeHeroSection = styled(HomeSection)`
  padding-right: 0;
`;

const HomeSideImageTop = styled.div`
  margin-bottom: 100px;
  @media (max-width: ${BREAKPOINT_SMALL}px) {
    margin-bottom: 50px;
  }
`;

const HomeHeroImageColumn = styled(HomeImageColumn)`
  align-self: flex-start;
  margin-top: 40px;
  @media (min-width: ${BREAKPOINT_SMALL}px) {
    margin-left: 25%;
  }
`;

const HomeHeroTitle = styled(MarketingHeading1)`
  margin: 150px 0 40px;

  @media (max-width: ${BREAKPOINT_SMALL}px) {
    margin-top: 40px;
  }
`;

const HomeHeroSubtitle = styled.h2`
  margin-bottom: 250px;
  font-weight: normal;
  font-size: 2.25rem;
  line-height: 1.25;

  @media (max-width: ${BREAKPOINT_SMALL}px) {
    margin-bottom: 50px;
    font-size: 2rem;
  }
`;

const HomeHeroAppButtons = styled.div`
  display: none;

  @media (max-width: ${BREAKPOINT_SMALL}px) {
    &:not(:empty) {
      display: block;
      margin-bottom: 50px;
    }
  }
`;
